// Header notification bell: unread badge (60s poll + window focus),
// dropdown with the 10 most recent, deep links via data.action_url.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BellIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { AppNotification } from '../../types';
import { timeAgo } from '../../utils/timeAgo';

const POLL_MS = 60_000;

export default function NotificationBell() {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);

  const refreshCount = useCallback(async () => {
    try {
      setUnread(await apiService.getUnreadNotificationCount());
    } catch {
      // background poll: fail silently
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, POLL_MS);
    window.addEventListener('focus', refreshCount);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', refreshCount);
    };
  }, [refreshCount]);

  const openPanel = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    setLoading(true);
    try {
      setItems(await apiService.getNotifications({ limit: 10 }));
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (n: AppNotification) => {
    setOpen(false);
    if (!n.is_read) {
      setUnread((c) => Math.max(0, c - 1));
      try {
        await apiService.markNotificationRead(n.id);
      } catch {
        // non-fatal
      }
    }
    if (n.data?.action_url) navigate(n.data.action_url);
  };

  const handleMarkAllRead = async () => {
    setUnread(0);
    setItems((rows) => rows.map((r) => ({ ...r, is_read: true })));
    try {
      await apiService.markAllNotificationsRead();
    } catch {
      // non-fatal
    }
  };

  return (
    <div className="relative">
      <button
        onClick={openPanel}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        className="icon-btn relative p-2 text-secondary-400 hover:text-ink-900 hover:bg-secondary-100 transition-colors"
      >
        <BellIcon className="w-6 h-6" />
        {unread > 0 && (
          <span
            data-testid="unread-badge"
            className="absolute top-0.5 right-0.5 min-w-[1.1rem] h-[1.1rem] px-0.5 bg-gold flex items-center justify-center font-display text-[0.65rem] leading-none text-ink-900"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white border border-ink-900 shadow-sm z-20">
            <div className="px-4 py-2.5 border-b border-ink-900 flex items-center justify-between">
              <span className="font-display text-sm uppercase tracking-wider text-ink-900">Notifications</span>
              <button
                onClick={handleMarkAllRead}
                className="text-xs font-semibold uppercase tracking-wider text-secondary-500 hover:text-ink-900"
              >
                Mark all read
              </button>
            </div>

            <div className="max-h-96 overflow-y-auto divide-y divide-secondary-200">
              {loading ? (
                <p className="px-4 py-6 text-sm text-secondary-500">Loading…</p>
              ) : items.length === 0 ? (
                <p className="px-4 py-6 font-display text-sm uppercase tracking-wider text-secondary-400 text-center">
                  Nothing on record.
                </p>
              ) : (
                items.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => handleRowClick(n)}
                    className={`block w-full text-left px-4 py-3 hover:bg-secondary-50 transition-colors ${
                      n.is_read ? '' : 'border-l-2 border-gold bg-gold/5'
                    }`}
                  >
                    <p className={`text-sm ${n.is_read ? 'text-secondary-700' : 'font-semibold text-ink-900'}`}>
                      {n.title}
                    </p>
                    {n.message && (
                      <p className="text-xs text-secondary-500 line-clamp-2 mt-0.5">{n.message}</p>
                    )}
                    <p className="text-xs text-secondary-400 mt-1">{timeAgo(n.created_at)}</p>
                  </button>
                ))
              )}
            </div>

            <div className="border-t border-secondary-200">
              <Link
                to="/app/notifications"
                onClick={() => setOpen(false)}
                className="block px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-secondary-700 hover:bg-ink-900 hover:text-paper transition-colors"
              >
                View all
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
